import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Clock, ShareNetwork, Flask } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { API_URL } from '../config/api';
import { SEO } from '../components/SEO';

const BlogPostPage = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/blog/posts/${slug}`)
      .then(r => setPost(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  const share = async () => {
    const url = `${window.location.origin}/blog/${slug}`;
    if (navigator.share) {
      try { await navigator.share({ title: post.title, text: post.excerpt, url }); } catch {}
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Link copied');
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6">
        <h1 className="text-2xl font-bold mb-2">Post not found</h1>
        <p className="text-sm text-muted-foreground mb-4">This post may have been moved or unpublished.</p>
        <Button onClick={() => navigate('/blog')}>Back to blog</Button>
      </div>
    );
  }
  if (!post) return <div className="min-h-screen bg-background flex items-center justify-center"><div className="text-sm text-muted-foreground">Loading…</div></div>;

  // JSON-LD Article schema for Google rich results
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": post.title,
    "description": post.excerpt,
    "author": { "@type": "Person", "name": post.author_name || "Derwin" },
    "publisher": { "@type": "Organization", "name": "TurfPulse", "url": "https://getturfpulse.com" },
    "datePublished": post.published_at,
    "dateModified": post.updated_at,
    "image": post.hero_image_url,
    "mainEntityOfPage": `https://getturfpulse.com/blog/${post.slug}`,
  };

  return (
    <div className="min-h-screen bg-background" data-testid="blog-post-page">
      <SEO title={`${post.title} — TurfPulse Blog`} description={post.excerpt} image={post.hero_image_url} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />

      <header className="border-b border-border/60 bg-card/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-3xl mx-auto flex items-center justify-between px-4 py-3">
          <button onClick={() => navigate('/blog')} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="blog-post-back-btn">
            <ArrowLeft size={14} /> All posts
          </button>
          <Button variant="ghost" size="sm" onClick={share} data-testid="blog-post-share-btn">
            <ShareNetwork size={14} className="mr-1" /> Share
          </Button>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-4 py-10 space-y-6">
        {post.category && <div className="text-xs font-semibold uppercase tracking-wider text-primary" data-testid="blog-post-category">{post.category}</div>}
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight leading-tight">{post.title}</h1>
        {post.subtitle && <p className="text-xl text-muted-foreground italic">{post.subtitle}</p>}

        <div className="flex items-center gap-4 text-xs text-muted-foreground border-b border-border/40 pb-4">
          <span>By <strong className="text-foreground">{post.author_name}</strong></span>
          <span className="flex items-center gap-1"><Clock size={11} /> {post.read_time_minutes} min read</span>
          {post.published_at && <span>· {new Date(post.published_at).toLocaleDateString()}</span>}
        </div>

        {post.hero_image_url && (
          <div className="rounded-lg overflow-hidden border border-border/40 my-4">
            <img src={post.hero_image_url} alt={post.title} className="w-full h-auto" />
          </div>
        )}

        <div className="prose prose-neutral dark:prose-invert max-w-none prose-headings:font-bold prose-h2:text-2xl prose-h2:mt-8 prose-h2:border-b prose-h2:pb-1 prose-h2:border-border/40 prose-h3:text-lg prose-a:text-primary prose-table:text-sm" data-testid="blog-post-body">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.body}</ReactMarkdown>
        </div>

        {post.related_experiment && (
          <Card className="border-primary/30 bg-primary/5 mt-8" data-testid="blog-post-experiment">
            <CardContent className="p-4 flex items-start gap-3">
              <Flask size={18} weight="fill" className="text-primary shrink-0 mt-0.5" />
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-primary mb-1">From the Lab Notebook</div>
                <p className="text-sm">This post documents <strong>{post.related_experiment}</strong> — part of the TurfPulse Lab's 12-experiment series chasing superintendent-grade turf on a residential test lawn.</p>
              </div>
            </CardContent>
          </Card>
        )}

        {post.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-4 border-t border-border/40" data-testid="blog-post-tags">
            {post.tags.map(t => (
              <Link key={t} to={`/blog?tag=${t}`} className="text-xs px-2.5 py-1 rounded-full bg-muted text-muted-foreground hover:bg-primary/10 hover:text-primary">{t}</Link>
            ))}
          </div>
        )}

        <div className="pt-6 border-t border-border/40 flex flex-col sm:flex-row gap-2">
          <Button variant="outline" className="flex-1" onClick={() => navigate('/blog')} data-testid="blog-post-more">Read more posts</Button>
          <Button className="flex-1" onClick={() => navigate('/hey')} data-testid="blog-post-cta-hey">Try TurfPulse for your ZIP →</Button>
        </div>
      </article>
    </div>
  );
};

export default BlogPostPage;
